import { getAttrFromProductByName } from '../../../../types/attrNames';
import { CmsProduct } from '../../../../types/cmsProduct';
import { getTiAttributeValue } from '../../../../utils/tiAttributes';

/**
 * One resolved eligibility config row supplied by the fe wrapper (mapped from the Strapi
 * `ti-eligibility` component). `attributeKey` is the catalogue attribute NAME (matched against the
 * active product's attributes); `label` is the attribute's own catalogue label; the editor can
 * override it via `displayLabelOverride` and force-hide the row via `visible: false`.
 */
export type TiEligibilityRowConfig = {
  attributeKey: string;
  label?: string;
  displayLabelOverride?: string;
  visible?: boolean;
};

export type TiEligibilityEntry = {
  key: string;
  label: string;
  value: string;
};

/**
 * Resolves the eligibility config rows against the active product into the rows that should actually
 * render, preserving config order. A row is dropped when it is force-hidden (`visible === false`) or
 * the product has no value for its attribute (all-rows auto-hide, no "—" placeholder —
 * CMS-1644 §6.3 / AC-6). Single source of truth for both section visibility and the rendered list.
 */
export function resolveTiEligibilityEntries(
  product: CmsProduct | undefined,
  rows: TiEligibilityRowConfig[] | undefined,
): TiEligibilityEntry[] {
  if (!product || !rows?.length) return [];

  return rows
    .filter((row) => row.visible !== false)
    .map((row) => ({
      key: row.attributeKey,
      label:
        row.displayLabelOverride ||
        getAttrFromProductByName(row.attributeKey, product)?.label ||
        row.label ||
        row.attributeKey,
      value: getTiAttributeValue(product, row.attributeKey),
    }))
    .filter((entry): entry is TiEligibilityEntry => entry.value !== undefined);
}
